import React from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';

import Slider from 'react-slick';

import CarouselPagingToolbar from './CarouselPagingToolbar';
import CarouselThumbnailToolbar from './CarouselThumbnailToolbar';

import * as AssetUtils from 'display/common/utils/asset';

import { getLabel } from 'display/common/utils/string';

import PostCard from '../../../../../common/components/displayItems/items/PostCard';

/**
 *  Display Component for Social Content Carousel
 *
 *  @author Clay Kirk
 **/
class SocialCarousel extends React.Component {
  constructor(props) {
    super(props);
    this.next = this.next.bind(this);
    this.previous = this.previous.bind(this);
    this.goto = this.goto.bind(this);
    this.slideUpdated = this.slideUpdated.bind(this);
    this.getRef = this.getRef.bind(this);
    this.handleResize = this.handleResize.bind(this);
    const thumbnails = (showThumbnails(props.template) && getThumbnails(props.children)) || undefined;
    this.state = { currentSlide: 0, slideHeight: 0, containerHeight: 0, thumbnails, isHidden: false };
    this.slides = [];
  }

  next() {
    this.slider.slickNext();
  }
  previous() {
    this.slider.slickPrev();
  }
  goto(e, slideNumber) {
    e.preventDefault();
    this.slider.slickGoTo(slideNumber);
    this.setState({ currentSlide: slideNumber });
  }
  slideUpdated(currentSlide) {
    this.setState({ currentSlide });
  }
  componentDidMount() {
    const containerWidth = this.container.offsetWidth;
    if (containerWidth === 0) {
      this.setState({ isHidden: true });
    }
    window.addEventListener('resize', this.handleResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, false);
  }
  componentDidUpdate() {
    const containerWidth = this.container.offsetWidth;

    if (containerWidth === 0 && !this.state.isHidden) {
      this.setState({ isHidden: true });
    }
    if (containerWidth !== 0 && this.state.isHidden) {
      this.setState({ isHidden: false });

      /*   offsetHeight is not defined if I call handleResize right away
           so delaying a bit.
      */
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'), 0);
      }, 100);
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.children !== nextProps.children) {
      const thumbnails = (showThumbnails(nextProps.template) && getThumbnails(nextProps.children)) || undefined;
      // if newItems, adjust currentIndex to remain on same active item
      if (!_.isEqual(this.props.newItems, nextProps.newItems) && !_.isEmpty(nextProps.newItems)) {
        const adjustedSlideNumber = this.state.currentSlide + nextProps.newItems.length % nextProps.children.length;
        this.slider.slickGoTo(adjustedSlideNumber);
        this.setState({ currentSlide: adjustedSlideNumber, thumbnails });
      } else {
        this.setState({ thumbnails });
      }
    }
  }
  handleResize(override) {
    if (this.state.isHidden) {
      return;
    }
    //  we are going to pass the 'containerHeight' from the preview iframe to the 'ItemContainer' to force a re-render
    //  when the iframe view (device mode) changes
    const containerHeight = _.get(window.frameElement, 'clientHeight', this.container.offsetHeight);

    //  we are going to calculate and set the largest slide height to mitigate the vertical scroll issue with dynamic slide heights
    const slideHeight = _.maxBy(this.slides, 'clientHeight').clientHeight;

    //  needed to update slideHeight and containerHeight separately
    if (this.state.slideHeight !== slideHeight) {
      this.setState({ slideHeight });
    }

    if (containerHeight !== this.state.containerHeight) {
      this.setState({ containerHeight });
    }
  }

  getRef(ref, i) {
    this.slides[i] = ref;
  }

  render() {
    const { autoAdvance, autoAdvanceSpeed, children, animation, moduleId } = this.props;
    const { thumbnails } = this.state;

    const vertical = isVertical(animation);
    const fade = isFade(animation);

    const settings = {
      autoplaySpeed:  autoAdvanceSpeed,
      dots:           false,
      vertical,
      fade,
      infinite:       true,
      slidesToShow:   1,
      slidesToScroll: 1,
      autoplay:       autoAdvance,
      useCSS:         true,
      adaptiveHeight: false,
      afterChange:    this.slideUpdated,
      prevArrow:      <HiddenArrow />,
      nextArrow:      <HiddenArrow />,
    };

    return (
      <div className={this.props.className} ref={c => (this.container = c)}>
        <Slider ref={c => (this.slider = c)} {...settings} className="xContainerInner">
          <For each="item" index="idx" of={children}>
            <div key={`${moduleId}-slide-${idx}`} className="xSlideContainer">
              <ItemContainer
                getRef={this.getRef}
                idx={idx}
                height={(!!vertical && this.state.slideHeight) || undefined}
              >
                {item}
              </ItemContainer>
            </div>
          </For>
        </Slider>
        <CarouselPagingToolbar
          showNext={this.next}
          showPrev={this.previous}
          nextLabel={getLabel('lbl.button.next', 'Next')}
          prevLabel={getLabel('lbl.button.previous', 'Previous')}
        />
        <If condition={!!thumbnails}>
          <CarouselThumbnailToolbar
            thumbnails={thumbnails}
            slideCount={children.length}
            currentSlide={this.state.currentSlide}
            showSlide={this.goto}
          />
        </If>
      </div>
    );
  }
}
SocialCarousel.displayName = 'SocialCarousel';

SocialCarousel.propTypes = {
  moduleId:         PropTypes.string.isRequired, //  needed to identify slides
  children:         PropTypes.array.isRequired,
  animation:        PropTypes.string,
  autoAdvance:      PropTypes.bool,
  template:         PropTypes.string,
  autoAdvanceSpeed: PropTypes.number,
  newItems:         PropTypes.arrayOf(PropTypes.element),
};

SocialCarousel.defaultProps = {
  animation:        'scrollhorz',
  template:         '',
  autoAdvance:      true,
  autoAdvanceSpeed: 7000,
};

//  Not sure if there's an another solution but I'm hiding the default arrows for react-slick by using this component.
//  CarouselPagingToolbar component will handle the paging
const HiddenArrow = () => <div style={{ display: 'none' }} />;

const getThumbnails = children => {
  return _.map(children, c => {
    const item = _.get(c.props, 'item', {});
    const assets = AssetUtils.getDisplayAssets(item, c.props.assetUrl, c.props.assetUgcUrl);
    const thumbnail = {
      src:    _.get(assets, 'thumbnail.src') || _.get(item, 'meta.network'),
      status: _.get(assets, 'thumbnail.meta.status') || null,
    };

    //  showing network icon if thumbnail not available (text only tweets)
    return thumbnail;
  });
};

//  making an assumption that if the template contains thumbnails then show them
const showThumbnails = template => template.includes('thumbnail');

const isVertical = animation => animation.toLowerCase() === 'scrollup';
const isFade = animation => animation.toLowerCase() === 'fade';

export default SocialCarousel;

/**
 *  A container to provide a ref to the child container for each slide.
 *  These refs allow the social carousel to compute the slide height used
 *  in 'handleResize' method above.
 *
 **/
class ItemContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }
  componentDidMount() {
    this.props.getRef(this.item, this.props.idx);
    //  triggering a resize event here to force the slider to resize
    //  to these children
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'), 0);
    }, 200);
  }
  render() {
    const style = !!this.props.height ? { height: `${this.props.height}px` } : {};
    const childrenWithStyleRestrictions = React.Children.map(this.props.children, child => {
      if (child.type === PostCard) {
        return React.cloneElement(child, {
          mediaWidth: clientWidth => Math.floor(clientWidth / 2),
        });
      } else {
        return child;
      }
    });
    return (
      <div ref={n => (this.item = n)} className="xSlideItemContainer" style={style}>
        {childrenWithStyleRestrictions}
      </div>
    );
  }
}

ItemContainer.propTypes = {
  children: PropTypes.object.isRequired,
  height:   PropTypes.number,
};
